import fetch from 'isomorphic-fetch';
import oboe from 'oboe';
import download from 'downloadjs';

import storageHelper from './storageHelper';
import { isWallboard } from './pageRules';

export const CLIENT_ROOT_URL =
  typeof window === 'undefined' || process.env.RUN_ENV === 'test'
    ? process.env.BASE_URL || `http://localhost:${process.env.PORT || 8080}/`
    : '/';

export const API_URL =
  typeof window === 'undefined' || process.env.RUN_ENV === 'test'
    ? process.env.BASE_URL || `http://localhost:${process.env.PORT || 8080}/api`
    : '/api';

export const AUTH_URL =
  typeof window === 'undefined' || process.env.RUN_ENV === 'test'
    ? process.env.BASE_URL || `http://localhost:${process.env.PORT || 8080}/auth`
    : '/auth';

function checkForUpdates(appVer) {
  if (appVer) {
    const oldVer = storageHelper.storage().getItem('ver');
    if (!!oldVer && oldVer !== appVer) {
      storageHelper.storage().setItem('ver', appVer);
      global.window && window.location && window.location.reload();
    } else if (!oldVer) {
      storageHelper.storage().setItem('ver', appVer);
    }
  }
}

function getHeaders(formData) {
  const headers = formData ? {} : { 'content-type': 'application/json' };
  const wallboard = isWallboard(window.location.pathname);
  const id = wallboard && window.location.pathname.split('/').slice(-2)[0];
  const token = getToken();
  const displayToken = getDisplayToken(id);

  if (!token && !displayToken) return headers;

  if (wallboard) {
    headers['Authorization'] = displayToken || `Bearer wallboard_${token}`;
  } else {
    headers['Authorization'] = token !== null ? `Bearer ${token}` : displayToken;
  }

  return headers;
}

function getToken() {
  return storageHelper.storage().getItem('token') || null;
}

function getDisplayToken(id) {
  return storageHelper.storage().getItem(id ? `displayToken_${id}` : 'displayToken') || null;
}

function request(url, method, body, opt = {}) {
  if (!opt.formData && body) {
    body = JSON.stringify(body);
  }

  return fetch(url, {
    headers: getHeaders(opt.formData),
    method,
    body
  })
    .then(response => {
      checkForUpdates(response.headers.get('X-App-Version'));

      if (response.status === 401) {
        storageHelper.storage().removeItem('token');
        storageHelper.storage().removeItem('displayToken');
        global.window && window.location && window.location.replace('/login' + window.location.search);
        return Promise.reject();
      }

      if (response.status === 502) {
        global.window && window.location && window.location.replace('/downtime');
        return Promise.reject();
      }

      if (response.redirected) {
        global.window && window.location && window.location.replace(response.url);
        return Promise.reject();
      }

      return response
        .json()
        .then(json => ({ json, response }))
        .catch(err => {
          return {
            json: { message: 'Empty response' },
            response: { ...response, empty: true, ok: false }
          };
        });
    })
    .then(({ json, response }) => {
      if (response.empty) {
        return Promise.reject();
      }
      if (!response.ok) {
        if (json) return Promise.reject(json);
      }

      return json;
    })
    .catch(err => {
      if (err && err.toString().indexOf('Failed to fetch') === -1) {
        return Promise.reject(err);
      }
      return Promise.reject();
    });
}

export function api(endpoint, method = 'get', body = undefined) {
  return request(`${API_URL}/${endpoint}`, method, body);
}

export function apiWithFormData(endpoint, body) {
  return request(`${API_URL}/${endpoint}`, 'post', body, {
    formData: true
  });
}

export function auth(endpoint, method = 'get', body) {
  return request(`${AUTH_URL}/${endpoint}`, method, body);
}

export function stream(endpoint, method = 'get') {
  return oboe({
    url: `${API_URL}/${endpoint}`,
    method: method,
    headers: getHeaders()
  });
}

export function report(endpoint, method = 'get', body = undefined) {
  return fetch(`${API_URL}/reports/${endpoint}`, {
    headers: getHeaders(),
    method,
    body: body ? JSON.stringify(body) : undefined
  }).then(response => {
    if (response.status === 401) {
      storageHelper.storage().removeItem('token');
      storageHelper.storage().removeItem('displayToken');
      global.window && window.location && window.location.replace('/login' + window.location.search);
      return Promise.reject();
    }

    if (response.status === 502) {
      global.window && window.location && window.location.replace('/downtime');
      return Promise.reject();
    }

    return response.blob();
  });
}

export function downloadFile(url, name) {
  return fetch(url, {
    method: 'GET',
    responseType: 'blob'
  })
    .then(res => {
      return res.blob();
    })
    .then(blob => {
      return download(blob, name, 'application/pdf');
    });
}
